import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Chip, ClickAwayListener, IconButton, Popper, styled } from '@mui/material';
import React, { useRef, useState } from 'react';

import { WsMenuList } from '../../components/WsMenuList';
import { commonPopperModifiers } from '../../types/CommonPopperModifiers';
import {
  WsFontWeight,
  WsBadgeSize,
  WsBadgeProps,
  WsBadgeTypeProps,
  WsBadgetype,
} from '../../WsEnums';

import EnhancedEndIcon from './EnhancedEndIcon';
import EnhancedStartIcon from './EnhancedStartIcon';

export interface IWsBadge {
  label: string;
  icon?: React.ReactElement;
  badgeSize?: WsBadgeSize;
  badgeType?: WsBadgetype;
  onClick?: (() => void) | ((event: React.SyntheticEvent) => void);
  startIcon?: boolean;
  endIcon?: boolean;
  menuList?: boolean;
  onItemClick?: (index: string) => void;
  menuOptions?: string[];
}
const AnchorWrapper = styled('span')``;

const LabelWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MenuWrapper = styled(Box)``;

const StyledPopper = styled(Popper)`
  z-index: 1300;
`;

const StyledChip = styled(Chip)<{
  textColor: string;
  backgroundColor: string;
  textSize: string;
  fontWeight: number;
  badgeHeight: string;
}>(({ textColor, backgroundColor, textSize, fontWeight, badgeHeight }) => ({
  color: textColor,
  backgroundColor: backgroundColor,
  borderRadius: 15,
  fontSize: textSize,
  fontWeight: fontWeight,
  height: badgeHeight,
}));

const StyledIconButton = styled(IconButton)({
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const WsBadge = ({
  label = 'WsBadge',
  icon,
  badgeSize = WsBadgeSize.MEDIUM,
  badgeType = WsBadgetype.SUCCESS,
  onClick,
  startIcon = false,
  endIcon = false,
  menuList = false,
  onItemClick,
  menuOptions = [],
}: IWsBadge) => {
  const { badgeHeight, textSize, startIconSize, endIconSize } = WsBadgeProps[badgeSize];
  const { textColor, backgroundColor } = WsBadgeTypeProps[badgeType];

  const [listOpenStatus, setListOpenStatus] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const handleMenuList = () => setListOpenStatus((prev) => !prev);
  const handleClose = () => setListOpenStatus(false);

  return (
    <>
      <AnchorWrapper ref={anchorRef}>
        <StyledChip
          label={
            <LabelWrapper>
              {label?.length > 24 ? label.substring(0, 24) + '...' : label}
              {endIcon && (
                <StyledIconButton size="small" onClick={onClick}>
                  <EnhancedEndIcon
                    endIcon={endIcon}
                    icon={icon}
                    textColor={textColor}
                    endIconSize={endIconSize}
                  />
                </StyledIconButton>
              )}
              {menuList && (
                <StyledIconButton size="small" onClick={handleMenuList}>
                  {listOpenStatus ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </StyledIconButton>
              )}
            </LabelWrapper>
          }
          icon={
            <EnhancedStartIcon
              icon={icon}
              textColor={textColor}
              startIcon={startIcon}
              startIconSize={startIconSize}
            />
          }
          textColor={textColor}
          backgroundColor={backgroundColor}
          textSize={textSize}
          fontWeight={WsFontWeight.REGULAR}
          badgeHeight={badgeHeight}
        />
      </AnchorWrapper>

      <StyledPopper
        open={listOpenStatus}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={commonPopperModifiers}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <MenuWrapper>
            <WsMenuList
              menuOptions={menuOptions}
              onItemClick={(index) => onItemClick?.(menuOptions[index])}
            />
          </MenuWrapper>
        </ClickAwayListener>
      </StyledPopper>
    </>
  );
};

export default WsBadge;
